import React, { useState, useEffect, useRef } from "react";

import { Grid, IconButton } from "@mui/material";
import { Add, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FiMapPin } from "react-icons/fi";
import { v4 as uuid } from "uuid";

import { useAppState } from "../../../../context/StateContext";
import {
  DifferentLocation,
  DifferentLocation as LocationAddress,
} from "../../../../models/location";
import Button, { Color } from "../../../Form/Button";
import Input from "../../../Form/Input";
import MapInput from "../../../Form/MapInput";
import useStyles, { AddressWrapper } from "./styles";
import { GO_SHARE_COMPANY_IDS, GOSSELIN_MOVING_COMPANY_IDS } from "Constants";

interface Props {
  addressesArray: LocationAddress[];
  condition: string;
  tab?: string;
  view?: boolean;
  isExpandedAddress: boolean;
  setIsExpandedAddress?: (parameters: boolean) => void;
  update: (value: LocationAddress[]) => void;
  onRemoveAddress?: (from: string, locationIndex: number) => void;
  hasError?: boolean;
  errorMessage?: string;

  labelText?: boolean;
  containerClass?: string;
  hasToShowOnlyOne?: boolean;
  hasToShowMapIcon?: boolean;
  showMapIcon?: boolean;
  dataCy?: string;
  title?: string;
  required?: boolean;
  disableMapInput?: boolean;
}

const AddressInfoForm = ({
  addressesArray,
  condition,
  isExpandedAddress,
  tab,
  setIsExpandedAddress,
  update,
  onRemoveAddress,
  view = false,
  hasToShowOnlyOne = false,
  containerClass,
  showMapIcon = true,
  hasError,
  errorMessage = "",
  dataCy,
  title,
  required = false,
  disableMapInput = false,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [isUsingKeyboard, setIsUsingKeyboard] = useState<boolean[]>([]);
  const [addressForm, setAddressForm] = useState<LocationAddress[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<any>(0);
  const isInitialized = useRef(false);
  const addressFormRef = useRef<LocationAddress[]>([]);

  const companiesThatManuallyEntryAddress = ["gosselin moving", "gosselin govlog", "goshare"];

  const handleUpdateAddressFields = (attribute: string, index: number, value: string | number) => {
    const addressToUpdate = [...addressForm];
    addressToUpdate[index] = { ...addressToUpdate[index], [attribute]: value };

    if (attribute === "longAddress" && !String(value).trim()) {
      addressToUpdate[index] = {
        ...addressToUpdate[index],
        addressLine1: "",
        addressLine2: "",
        number: "",
        suburb: "",
        country: "",
        postalcode: "",
        state: "",
        longAddress: "",
      };
      setAddressForm([...addressToUpdate]);
      update(addressToUpdate);
      return true;
    }

    if (attribute !== "longAddress") {
      const addressLine = addressToUpdate[index]?.addressLine1
        ? `${addressToUpdate[index]?.addressLine1}`
        : "";
      const number = addressToUpdate[index]?.number ? ` ${addressToUpdate[index]?.number}` : "";
      const suburb = addressToUpdate[index]?.suburb ? `, ${addressToUpdate[index]?.suburb}` : "";
      const stateOrLine2 =
        addressToUpdate[index]?.state || addressToUpdate[index]?.addressLine2
          ? ` - ${addressToUpdate[index]?.state || addressToUpdate[index]?.addressLine2}`
          : "";
      const postalcode = addressToUpdate[index]?.postalcode
        ? `, ${addressToUpdate[index]?.postalcode}`
        : "";
      const country = addressToUpdate[index]?.country ? `, ${addressToUpdate[index]?.country}` : "";
      addressToUpdate[
        index
      ].longAddress = `${addressLine}${number}${suburb}${stateOrLine2}${postalcode}${country}`;
    }

    setAddressForm([...addressToUpdate]);
  };

  const handleGettingAddressFromGoogleMap = (location: DifferentLocation, index: number) => {
    const addressToUpdate = [...addressForm];

    if (!location.longAddress?.trim()) {
      addressToUpdate[index] = {
        ...addressToUpdate[index],
        addressLine1: "",
        addressLine2: "",
        number: "",
        suburb: "",
        country: "",
        postalcode: "",
        state: "",
        longAddress: "",
      };
    } else {
      addressToUpdate[index] = {
        ...addressToUpdate[index],
        ...location,
      };
    }

    setAddressForm(addressToUpdate);
    update(addressToUpdate);
  };

  const handleDeleteAddressFromArray = (index: number) => {
    if (onRemoveAddress) {
      onRemoveAddress(condition, index);
    }
  };

  const toggleInputType = (index: number) => {
    const selecteds = [...isUsingKeyboard];
    selecteds[index] = !selecteds[index];
    setIsUsingKeyboard([...selecteds]);

    const currentAddress = addressFormRef.current[index];
    const addressToUpdate = [...addressFormRef.current];

    if (!currentAddress?.longAddress?.trim()) {
      addressToUpdate[index] = {
        ...addressToUpdate[index],
        addressLine1: "",
        addressLine2: "",
        number: "",
        suburb: "",
        country: "",
        postalcode: "",
        state: "",
        longAddress: "",
      };
    } else {
      addressToUpdate[index] = {
        ...addressToUpdate[index],
        state: "",
      };
    }

    setAddressForm(addressToUpdate);
    update(addressToUpdate);

    if (setIsExpandedAddress && !isExpandedAddress) {
      setIsExpandedAddress(true);
    }
  };

  const getEntryByUsingMaps = (index: number) => (isUsingKeyboard[index] ? "Google" : "Manual");

  const onAddNewAddressEmpty = () => {
    const addressFormUpdated = [...addressForm];

    addressFormUpdated.push({
      id: uuid(),
      number: "",
      addressLine1: "",
      addressLine2: "",
      suburb: "",
      country: "",
      postalcode: "",
      longAddress: "",
    });
    update(addressFormUpdated);
  };

  useEffect(() => {
    if (addressForm.length > 0 && typeof addressForm[0].id === "number") {
      if (currentUser?.company.name.trim().length) {
        const companyName = currentUser?.company?.name;
        const showManuallyEntryAddress = GO_SHARE_COMPANY_IDS.concat(
          GOSSELIN_MOVING_COMPANY_IDS
        ).includes(currentUser?.companyId!);
        if (showManuallyEntryAddress) if (!isUsingKeyboard[0]) toggleInputType(0);
      }
    }
  }, [addressForm]);

  useEffect(() => {
    addressFormRef.current = addressForm;
  }, [addressForm]);

  useEffect(() => {
    if (!isInitialized.current) {
      if (addressesArray.length > 0) {
        setAddressForm(addressesArray);
      } else {
        setAddressForm([
          {
            id: uuid(),
            number: "",
            addressLine1: "",
            addressLine2: "",
            suburb: "",
            country: "",
            postalcode: "",
            longAddress: "",
          },
        ]);
      }
      isInitialized.current = true;
    }
  }, [addressesArray]);

  useEffect(() => {
    if (addressForm.length > 0 && isUsingKeyboard.length !== addressForm.length) {
      const initialKeyboardState = addressForm.map(() => false);
      setIsUsingKeyboard(initialKeyboardState);
    }
  }, [addressForm, isUsingKeyboard.length]);

  return (
    <AddressWrapper
      tabAccess={!!(tab && tab === "access")}
      isExpandedAddress={isExpandedAddress}
      isManualAddress={isUsingKeyboard[0]}
      data-cy="address-info-form"
      className={`${containerClass || ""}`}
    >
      {!title && (
        <div
          className={`d-flex align-items-center justify-content-between mb-3 ${
            tab && tab === "access" && !isExpandedAddress ? classes.addressInfoBorder : ""
          }`}
        >
          <div className="d-flex align-items-center ">
            <p className="d-flex flex-row align-items-center mb-0 mr-3">
              <span className={`mr-1 ${required ? classes.onboardingBlueLabel : ""}`}>
                {translate("address")}
                {required && <span style={{ color: "#D92D20", marginLeft: "4px" }}>*</span>}
              </span>
              <FiMapPin />
            </p>
            {!view && (
              <Button
                data-cy="button-googlemaps"
                className={`mr-2`}
                customColor={Color.SECONDARY}
                onClick={() => toggleInputType(0)}
              >
                {translate(`switchTo${getEntryByUsingMaps(0)}`)}
              </Button>
            )}
            {!hasToShowOnlyOne && (
              <Button
                data-cy="button-addnewaddress"
                customColor={Color.SECONDARY}
                onClick={onAddNewAddressEmpty}
                startIcon={<Add />}
                disabled={view}
              >
                {translate("addAnotherAddress")}
              </Button>
            )}
          </div>
          {tab && tab === "access" && (
            <IconButton
              onClick={() => setIsExpandedAddress && setIsExpandedAddress(!isExpandedAddress)}
            >
              {isExpandedAddress ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </div>
      )}
      {addressForm?.map(
        (address, index: number) =>
          !address.id?.toString().includes("delete") && (
            <div data-cy="address-form" key={index}>
              {index !== 0 && isExpandedAddress && (
                <div>
                  <hr className={classes.hr} />
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    className="mt-4 mb-3"
                    style={{ paddingLeft: ".55rem" }}
                  >
                    <Button
                      className={`mr-2`}
                      customColor={Color.SECONDARY}
                      onClick={() => toggleInputType(index)}
                    >
                      {translate(`switchTo${getEntryByUsingMaps(index)}`)}
                    </Button>
                    {true && (
                      <Button
                        customColor={Color.ERROR}
                        data-cy={`button-delete-address-${index + 1}`}
                        onClick={() => handleDeleteAddressFromArray(index)}
                      >
                        {translate("delete")}
                      </Button>
                    )}
                  </Grid>
                </div>
              )}
              {isUsingKeyboard[index] ? (
                <>
                  {isExpandedAddress && (
                    <Grid
                      data-cy="address-fields"
                      container
                      direction="column"
                      spacing={2}
                      justifyContent="space-between"
                    >
                      <Grid
                        item
                        container
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="streetNumber"
                            placeholder="123"
                            type="text"
                            label={translate("streetNumber")}
                            required={required}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address?.number}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("number", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="streetName"
                            placeholder="Lorem St."
                            type="text"
                            label={translate("street")}
                            required={required}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address?.addressLine1}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("addressLine1", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="complement"
                            placeholder="Complement"
                            type="text"
                            label={translate("complement")}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address.addressLine2}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("addressLine2", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="suburbTown"
                            placeholder="Suburb/Town"
                            type="text"
                            label={translate("suburbTown")}
                            required={required}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address?.suburb}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("suburb", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="country"
                            placeholder="Country"
                            type="text"
                            label={translate("country")}
                            required={required}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address?.country}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("country", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                        <Grid item sm md={4} lg={4}>
                          <Input
                            name="postcode"
                            placeholder="Postcode"
                            type="text"
                            label={translate("postcode")}
                            required={required}
                            labelClassName={required ? classes.onboardingBlueLabel : undefined}
                            value={address?.postalcode}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (typingTimeout) clearTimeout(typingTimeout);
                              handleUpdateAddressFields("postalcode", index, event.target.value);
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  {isExpandedAddress && (
                    <Grid
                      data-cy="google-map-grid"
                      container
                      direction="column"
                      className={title ? "" : `mt-3`}
                      style={{ marginBottom: -10 }}
                    >
                      <Grid>
                        <MapInput
                          required={required}
                          error={hasError}
                          errorMessage={errorMessage}
                          bordercolor="#D92D20"
                          disabled={view || disableMapInput}
                          inputValue={address.longAddress}
                          onChange={(location) => {
                            handleGettingAddressFromGoogleMap(location, index);
                          }}
                          onChangeInput={(value) => {
                            if (typingTimeout) clearTimeout(typingTimeout);
                            const alreadyUpdated = handleUpdateAddressFields(
                              "longAddress",
                              index,
                              value
                            );
                            if (!alreadyUpdated) {
                              setTypingTimeout(
                                setTimeout(() => update(addressFormRef.current), 500)
                              );
                            }
                          }}
                          location={address}
                          mapIsOpen={false}
                          showMapIcon={showMapIcon}
                          dataCy={dataCy}
                          label={title}
                          disabledPlaceholder={translate("pleaseFillInTheCustomerInformationFirst")}
                        />
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </div>
          )
      )}
    </AddressWrapper>
  );
};

export default AddressInfoForm;
